import { useState } from 'react';

import { isEmailValid } from '../utils/validationUtils';

export function useInput(initialValue) {
  const [value, setValue] = useState(initialValue);

  return {
    value,
    setValue,
    inputProps: {
      value,
      onChange: event => {
        setValue(event.target.value);
      }
    }
  };
}

// This hook should be used when user restricted to type only integer numbers to any given input area.
// For using this hook, input type should be given as a text because replace method can be used only for strings
// For converting value string to number, we should not give number as a type to input area, hook can handle this, itself.
export const useIntegerInput = initialValue => {
  const [value, setValue] = useState(initialValue);

  return {
    value,
    setValue,
    inputProps: {
      value,
      onChange: event => {
        setValue(+event.target.value.replace(/\D/, ''));
      }
    }
  };
};

export const usePhoneInput = initialValue => {
  const [value, setValue] = useState(initialValue);

  const isNumericInput = event => {
    const key = event.keyCode;
    return (
      (key >= 48 && key <= 57) || // Allow number line
      (key >= 96 && key <= 105) // Allow number pad
    );
  };

  const isModifierKey = event => {
    const key = event.keyCode;
    return (
      event.shiftKey === true ||
      key === 35 ||
      key === 36 || // Allow Shift, Home, End
      key === 8 ||
      key === 9 ||
      key === 13 ||
      key === 46 || // Allow Backspace, Tab, Enter, Delete
      (key > 36 && key < 41) || // Allow left, up, right, down
      // Allow Ctrl/Command + A,C,V,X,Z
      ((event.ctrlKey === true || event.metaKey === true) &&
        (key === 65 || key === 67 || key === 86 || key === 88 || key === 90))
    );
  };

  const enforceFormat = event => {
    // Input must be of a valid number format or a modifier key, and not longer than ten digits
    if (!isNumericInput(event) && !isModifierKey(event)) {
      event.preventDefault();
    }
  };

  const formatToPhone = event => {
    if (isModifierKey(event)) {
      return value;
    }

    // I am lazy and don't like to type things more than once
    const { target } = event;
    const input = target.value.replace(/\D/g, '').substring(0, 10); // First ten digits of input only
    const areaCode = input.substring(0, 3);
    const middle = input.substring(3, 6);
    const last = input.substring(6, 10);

    if (input.length > 6) {
      target.value = `(${areaCode}) ${middle}-${last}`;
    } else if (input.length > 3) {
      target.value = `(${areaCode}) ${middle}`;
    } else if (input.length > 0) {
      target.value = `(${areaCode}`;
    }
    return target.value;
  };

  return {
    value,
    setValue,
    inputProps: {
      value,
      onKeyUp: enforceFormat,
      onChange: event => {
        const formattedString = formatToPhone(event);
        setValue(formattedString);
      }
    }
  };
};

export const useValidInput = (initialValue, customValidator) => {
  const [value, setValue] = useState(initialValue);
  const [hasError, setHasError] = useState(false);
  const [errorData, setErrorData] = useState('');

  return {
    value,
    setValue,
    hasError,
    errorData,
    inputProps: {
      value,
      onChange: event => {
        if (customValidator) {
          setHasError(!customValidator(event.target.value));
        }
        setValue(event.target.value);

        if (event.target.type === 'email') {
          setHasError(!isEmailValid(event.target.value));
        } else setHasError(!event.target.validity.valid);

        if (event.target.required && !event.target.value) {
          setHasError(true);
        }

        setErrorData({ name: event.target.id, validity: event.target.validity });
      }
    }
  };
};
